import { hasOptionalPropertyOfType } from "@ciaopeople/cp-shatsu/CustomTypes";
import { inEnum, isArrayOf, isBoolean, isNumber, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { DeviceSubscription } from "@api-commons/api/types/SubscriptionTypes.js";
import { NotificationDeviceType } from "@api-commons/api/types/SubscriptionTypes.js";
import { NotificationSystemType } from "@api-commons/api/types/SubscriptionTypes.js";
import { NotificationTopic } from "../api/commondata/TopicTreeData.js";
import { isNotificationTopic } from '../api/commondata/TopicTreeData.js';

export enum SNUStatus {
    NEVER_ASKED = "A",
    NOT_ANSWERED = "B",
    REFUSED = "C",
    ACCEPTED = "D",
    BLOCKED = "E",
    FP_MERGED = "F",
    UNASSIGNED = "G",
    NOT_USED = "O",
    UNKNOWN = "P"
}

export enum SNUEndpointType {
    ANDROID = "A",
    IOS = "I",
    CHROME = "C",
    FIREFOX = "F",
    SAFARI_APNS = "S",
    EDGE = "E",
    GENERIC_BROWSER = "U"

}

function endpointTypeToNotificationDeviceType(et: SNUEndpointType): NotificationDeviceType {
    if(et == SNUEndpointType.ANDROID) return NotificationDeviceType.ANDROID;
    else if (et == SNUEndpointType.IOS) return NotificationDeviceType.IOS;
    else if (et == SNUEndpointType.CHROME) return NotificationDeviceType.CHROME_BROWSER;
    else if (et == SNUEndpointType.FIREFOX) return NotificationDeviceType.FIREFOX_BROWSER;
    else if (et == SNUEndpointType.GENERIC_BROWSER) return NotificationDeviceType.GENERIC_WEBPUSH_BROWSER;
    else if (et == SNUEndpointType.EDGE) return NotificationDeviceType.EDGE_BROWSER;
    else if (et == SNUEndpointType.SAFARI_APNS) return NotificationDeviceType.SAFARI_MAC;
    else NotificationDeviceType.UNSPECIFIED;
}

function endpointTypeToNotificationSystem(et: SNUEndpointType): NotificationSystemType {
    if(et == SNUEndpointType.ANDROID || et == SNUEndpointType.IOS) return NotificationSystemType.Firebase;
    else if (et == SNUEndpointType.SAFARI_APNS) return NotificationSystemType.SafariWebPushAPNS;
    else return NotificationSystemType.WebPushVAPID;
}


function getDeviceNameForEndpointType(et: SNUEndpointType): string {
    if(et == SNUEndpointType.ANDROID) return "Android App";
    else if (et == SNUEndpointType.IOS) return "IOS App";
    else if (et == SNUEndpointType.CHROME) return "Chrome Browser";
    else if (et == SNUEndpointType.FIREFOX) return "Firefox Browser";
    else if (et == SNUEndpointType.GENERIC_BROWSER) return "Generic Browser";
    else if (et == SNUEndpointType.EDGE) return "Edge Browser";
    else if (et == SNUEndpointType.SAFARI_APNS) return "Safari MacOSX";
    else return "Unknown Device";
}


export function getSNUEndpointTypeFromBrowserName(browserName: string): SNUEndpointType {
    let et = SNUEndpointType.GENERIC_BROWSER;
    if (browserName.match(/chrome/gi)) {
        et = SNUEndpointType.CHROME;
    }
    else if (browserName.match(/(explorer|edge)/gi)) {
        et = SNUEndpointType.EDGE;
    }
    else if (browserName.match(/firefox/gi)) {
        et = SNUEndpointType.FIREFOX;
    }
    return et;
}

export function convertNotificationDeviceTypeToSNUEndpointType(nt: NotificationDeviceType): SNUEndpointType {
    if(nt == NotificationDeviceType.ANDROID) return SNUEndpointType.ANDROID;
    else if(nt == NotificationDeviceType.IOS) return SNUEndpointType.IOS;
    else if (nt == NotificationDeviceType.CHROME_BROWSER) return SNUEndpointType.CHROME;
    else if (nt == NotificationDeviceType.SAFARI_MAC) return SNUEndpointType.SAFARI_APNS;
    else if (nt == NotificationDeviceType.EDGE_BROWSER) return SNUEndpointType.EDGE;
    else if (nt == NotificationDeviceType.FIREFOX_BROWSER) return SNUEndpointType.FIREFOX;
    else return SNUEndpointType.GENERIC_BROWSER;
}

export interface PushNotificationSubscription {
    endpoint: string,
    keys: {
        [k: string]: string
    }
}

export function isPushNotificationSubscription(obj: any): obj is PushNotificationSubscription {
    // ?TODO: Raffinare
    return isObject(obj) && isString(obj.endpoint) && isObject(obj.keys); 
}


export interface SNUNotificationEndpoint {
    endpointType: SNUEndpointType,
    subscription?: PushNotificationSubscription,
    topic?: string|number
}


export function SNUEndpointToDeviceSubscription(snu: SNUNotificationEndpoint): DeviceSubscription {
    return {
        token: JSON.stringify(snu.subscription),
        deviceId: "NoId",
        deviceType: endpointTypeToNotificationDeviceType(snu.endpointType),
        notificationSystemType: endpointTypeToNotificationSystem(snu.endpointType),
        applicationId: "SNU",
        deviceName: getDeviceNameForEndpointType(snu.endpointType),
        enabled: true
    };
}

export function isSNUNotificationEndpoint(obj: any): obj is SNUNotificationEndpoint {
    return isObject(obj) && 
            inEnum(obj.endpointType, SNUEndpointType) && 
            hasOptionalPropertyOfType(obj, "subscription", isPushNotificationSubscription) &&
            hasOptionalPropertyOfType(obj, "topic", (a) => isString(a) || isNumber(a));
}



export interface SNUUserDataObject {
    id: number,
    follows: NotificationTopic[],
    endpoint?: SNUNotificationEndpoint,
    currentStatus: SNUStatus,
    unreadNotifications: number
}

export function isSNUUserDataObject(obj: any): obj is SNUUserDataObject {
    return isObject(obj) && 
            isNumber(obj.id) &&
            isArrayOf(obj.follows, isNotificationTopic) &&
            hasOptionalPropertyOfType(obj, "endpoint", isSNUNotificationEndpoint) &&
            inEnum(obj.currentStatus, SNUStatus) &&
            isNumber(obj.unreadNotifications);
}



export interface SNUCheckSubObject {
    ask: boolean,
    nextCheckIn: number
}

export function isSNUCheckSubObject(obj: any): obj is SNUCheckSubObject {
    return isObject(obj) && 
            isBoolean(obj.ask) && 
            isNumber(obj.nextCheckIn);
}

export interface SNUCreationResponse {

    token: string,
    user: SNUUserDataObject,
    check: SNUCheckSubObject
}

export function isSNUCreationResponse(obj: any): obj is SNUCreationResponse {
    return isObject(obj) && 
            isString(obj.token) && 
            isSNUUserDataObject(obj.user) && 
            isSNUCheckSubObject(obj.check);
}

export interface SNUOperationsResponse {
    user: SNUUserDataObject,
    check: SNUCheckSubObject,
    vicarToken?: string
}

export function isSNUOperationsResponse(obj: any): obj is SNUOperationsResponse {
    return isObject(obj) && 
            isSNUUserDataObject(obj.user) && 
            isSNUCheckSubObject(obj.check) && 
            hasOptionalPropertyOfType(obj, "vicarToken", isString);
}
